import React from 'react'
import ReportWrapper from '../ReportWrapper'
import ReportSectionHeading from '../ReportSectionHeading'
import CompanyNonBankingApplicationRecordsOverview from './CompanyNonBankingApplicationRecordsOverview'
import CompanyNonBankingApplicationRecordsChart from './CompanyNonBankingApplicationRecordsChart'
import { Stack, Table, Text } from '@mantine/core'
import { useTheme } from '../../../components/ThemeProvider'
import { useViewMode } from '../ViewModeProvider'

interface CompanyNonBankingApplicationRecordsProps {
  data: any
  title: string
  sectionIndex?: string
}

const institutionTypes = [
  { label: '传统小贷机构', count: 'nb_{prefix}_apply_sloan_count', org: 'nb_{prefix}_apply_sloan_org_count' },
  { label: '互联网小贷机构', count: 'nb_{prefix}_apply_nsloan_count', org: 'nb_{prefix}_apply_nsloan_org_count' },
  { label: '金融科技机构', count: 'nb_{prefix}_apply_fintech_count', org: 'nb_{prefix}_apply_fintech_org_count' },
  { label: '融租/担保机构', count: 'nb_{prefix}_apply_leagua_count', org: 'nb_{prefix}_apply_leagua_org_count' },
  { label: '供应链/保理机构', count: 'nb_{prefix}_apply_supcfwar_count', org: 'nb_{prefix}_apply_supcfwar_org_count' },
  { label: '其他机构', count: 'nb_{prefix}_apply_else_count', org: 'nb_{prefix}_apply_else_org_count' },
]

const periods = [
  { label: '近7天', prefix: 'd7' },
  { label: '近15天', prefix: 'd15' },
  { label: '近1月', prefix: 'm1' },
  { label: '近3月', prefix: 'm3' },
  { label: '近6月', prefix: 'm6' },
  { label: '近1年', prefix: 'm12' },
]

const getValue = (data: any, key: string) => {
  const value = data?.[key]
  if (value === null || value === undefined) return '0'
  if (typeof value === 'number') return value.toString()
  return value
}

export default function CompanyNonBankingApplicationRecords({
  data,
  title,
  sectionIndex,
}: CompanyNonBankingApplicationRecordsProps) {
  const theme = useTheme()
  const { viewMode } = useViewMode()

  return (
    <ReportWrapper>
      <ReportSectionHeading title={title} index={sectionIndex} />
      <CompanyNonBankingApplicationRecordsOverview data={data} />
      <div className="mt-2">
        <Text size="sm" fw={600} style={{ color: theme.accentTextColor }}>
          申请明细
        </Text>
        {viewMode === 'table' ? (
          <Stack gap="md" className="mt-3">
            {periods.map((period) => (
              <div key={period.label}>
                <Text size="xs" fw={600} className="text-gray-600 mb-2">
                  {period.label}
                </Text>
                <Table
                  highlightOnHover
                  verticalSpacing="sm"
                  withColumnBorders={false}
                  styles={{
                    table: { fontSize: '13px', tableLayout: 'fixed' },
                    th: {
                      backgroundColor: theme.surfaceMutedColor,
                      color: theme.accentTextColor,
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '40%' }}>机构类别</Table.Th>
                      <Table.Th style={{ width: '30%' }}>申请次数</Table.Th>
                      <Table.Th style={{ width: '30%' }}>申请机构数</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {institutionTypes.map((type) => {
                      const countKey = type.count.replace('{prefix}', period.prefix)
                      const orgKey = type.org.replace('{prefix}', period.prefix)
                      return (
                        <Table.Tr key={`${period.label}-${type.label}`}>
                          <Table.Td>{type.label}</Table.Td>
                          <Table.Td>{getValue(data, countKey)}</Table.Td>
                          <Table.Td>{getValue(data, orgKey)}</Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </Table>
              </div>
            ))}
          </Stack>
        ) : (
          <div className="mt-3">
            <CompanyNonBankingApplicationRecordsChart data={data} />
          </div>
        )}
      </div>
    </ReportWrapper>
  )
}
